import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  LayoutDashboard, 
  Sparkles,
  Users,
  MousePointer,
  TrendingUp,
  Globe,
  Smartphone,
  Monitor,
  BarChart3,
  Clock,
  MapPin,
  Bot,
  MessageSquare
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  resourcesData, 
  useUserCloud, 
  EmailCaptureModal,
  PromptBlock, 
  EmergentCTA,
  AboutEmergent,
  RelatedResources, 
  AuthorCard,
  resourceColors as colors 
} from '../ResourceComponents';

const resource = resourcesData['admin-dashboard'];

// Dashboard mockup component
const DashboardMockup = () => (
  <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg bg-gray-900 mb-12">
    {/* Window controls */}
    <div className="flex items-center gap-2 px-4 py-3 bg-gray-800 border-b border-gray-700">
      <div className="w-3 h-3 rounded-full bg-red-500" />
      <div className="w-3 h-3 rounded-full bg-yellow-500" />
      <div className="w-3 h-3 rounded-full bg-green-500" />
      <span className="ml-4 text-xs text-gray-400">yoursite.com/admin</span>
    </div>
    
    {/* Dashboard content */}
    <div className="p-6">
      {/* Stats row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { icon: Users, label: 'Visitors', value: '2,847', change: '+12%' },
          { icon: MousePointer, label: 'Page Views', value: '8,392', change: '+8%' },
          { icon: TrendingUp, label: 'Bounce Rate', value: '34%', change: '-5%' },
          { icon: Clock, label: 'Avg. Time', value: '2:34', change: '+15%' },
        ].map((stat) => (
          <div key={stat.label} className="p-4 rounded-xl bg-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-400">{stat.label}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-white">{stat.value}</span>
              <span className="text-xs text-green-400">{stat.change}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Traffic sources - updated to show AI chats */}
        <div className="col-span-2 p-4 rounded-xl bg-gray-800">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-white">Traffic Sources</span>
            <span className="text-xs text-gray-400">Last 7 days</span>
          </div>
          <div className="space-y-3">
            {[
              { source: 'Google Search', pct: 35, color: '#4285F4' },
              { source: 'AI Chats (ChatGPT, Claude)', pct: 28, color: '#9333EA', highlight: true },
              { source: 'Instagram', pct: 18, color: '#E4405F' },
              { source: 'Direct', pct: 12, color: '#00CED1' },
              { source: 'Other', pct: 7, color: '#6B7280' },
            ].map((item) => (
              <div key={item.source} className="flex items-center gap-3">
                <div className="w-24 text-xs text-gray-400 truncate flex items-center gap-1">
                  {item.highlight && <Bot className="w-3 h-3 text-purple-400" />}
                  {item.source}
                </div>
                <div className="flex-1 h-2 rounded-full bg-gray-700">
                  <div 
                    className="h-full rounded-full transition-all" 
                    style={{ width: `${item.pct}%`, background: item.color }}
                  />
                </div>
                <span className="text-xs text-white w-8">{item.pct}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Device breakdown */}
        <div className="p-4 rounded-xl bg-gray-800">
          <span className="text-sm font-medium text-white">Devices</span>
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-3">
              <Monitor className="w-4 h-4 text-blue-400" />
              <div className="flex-1">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-400">Desktop</span>
                  <span className="text-white">58%</span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-700">
                  <div className="h-full rounded-full bg-blue-400" style={{ width: '58%' }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Smartphone className="w-4 h-4 text-purple-400" />
              <div className="flex-1">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-400">Mobile</span>
                  <span className="text-white">42%</span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-700">
                  <div className="h-full rounded-full bg-purple-400" style={{ width: '42%' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Feature illustration component
const FeatureIllustration = ({ icon: Icon, title, items, color }) => (
  <div className="p-6 rounded-2xl border border-gray-100 bg-gradient-to-b from-gray-50 to-white">
    <div 
      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
      style={{ background: `${color}20` }}
    >
      <Icon className="w-6 h-6" style={{ color }} />
    </div>
    <h4 className="font-semibold text-gray-900 mb-3">{title}</h4>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const AdminDashboardPage = () => {
  const { userEmail, saveUser, saveResource } = useUserCloud();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (userEmail) {
      setHasAccess(true);
      saveResource(resource.id);
    }
  }, [userEmail]);

  const handleSaveToCloud = async (email) => {
    saveUser(email);
    saveResource(resource.id);
    setShowEmailModal(false);
    setHasAccess(true);
  };

  if (!hasAccess && !userEmail) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          <div 
            className="w-24 h-24 rounded-3xl mx-auto mb-6 flex items-center justify-center"
            style={{ background: `${resource.color}20` }}
          >
            <LayoutDashboard className="w-12 h-12" style={{ color: resource.color }} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-gray-600 mb-8">{resource.description}</p>
          
          <button
            onClick={() => setShowEmailModal(true)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ background: colors.teal }}
          >
            <Sparkles className="w-5 h-5" />
            Save to Your Cloud
          </button>
          
          <p className="text-gray-400 text-sm mt-4">
            Enter your email to access this resource
          </p>

          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mt-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Data Dreaming
          </Link>
        </div>

        <EmailCaptureModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSubmit={handleSaveToCloud}
          resourceTitle={resource.title}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Resource by</span>
            <span className="text-sm font-medium text-gray-900">{resource.author.handle}</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div 
        className="py-16 px-4"
        style={{ background: `linear-gradient(180deg, ${resource.color}10 0%, transparent 100%)` }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <span 
              className="px-3 py-1 rounded-full text-xs font-medium"
              style={{ background: `${resource.color}20`, color: resource.color }}
            >
              {resource.platform} Prompt
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-xl text-gray-600 max-w-2xl">{resource.description}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Dashboard Preview */}
        <div className="mb-12">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">What You'll Build</h3>
          <DashboardMockup />
        </div>

        {/* What to Track - Visual */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">What Should You Track?</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <FeatureIllustration 
              icon={BarChart3}
              title="Default (Recommended)"
              color="#00CED1"
              items={[
                'Page views & unique visitors',
                'Traffic sources (including AI chats)',
                'Device types',
                'CTA button clicks'
              ]}
            />
            <FeatureIllustration 
              icon={TrendingUp}
              title="Add If Relevant"
              color="#6A0DAD"
              items={[
                'YouTube video plays',
                'Newsletter signup clicks',
                'Contact form submissions',
                'Location data'
              ]}
            />
          </div>
        </div>

        {/* AI Traffic Sources Callout */}
        <div className="mb-12 p-6 rounded-2xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Bot className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                Track AI Chat Traffic
                <span className="px-2 py-0.5 rounded-full bg-purple-200 text-purple-700 text-xs font-medium">New Source</span>
              </h4>
              <p className="text-gray-600 text-sm mb-4">
                People are discovering websites through AI assistants like ChatGPT, Claude, and Perplexity. 
                Your dashboard can track when visitors come from these AI conversations.
              </p>
              <div className="flex flex-wrap gap-2">
                {['ChatGPT', 'Claude', 'Perplexity', 'Gemini', 'Copilot'].map(ai => (
                  <span key={ai} className="px-3 py-1 rounded-full bg-white border border-purple-200 text-xs font-medium text-purple-700 flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" />
                    {ai}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Flow diagram */}
        <div className="mb-12 p-6 rounded-2xl bg-gray-900 text-white">
          <h4 className="font-semibold mb-4">How It Works</h4>
          <div className="flex items-center justify-between text-center">
            <div className="flex-1">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-2">
                <Users className="w-6 h-6 text-teal-400" />
              </div>
              <p className="text-xs text-gray-400">Visitor arrives</p>
            </div>
            <div className="text-gray-600">→</div>
            <div className="flex-1">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-2">
                <MousePointer className="w-6 h-6 text-purple-400" />
              </div>
              <p className="text-xs text-gray-400">Actions tracked</p>
            </div>
            <div className="text-gray-600">→</div>
            <div className="flex-1">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-2">
                <BarChart3 className="w-6 h-6 text-pink-400" />
              </div>
              <p className="text-xs text-gray-400">Data stored</p>
            </div>
            <div className="text-gray-600">→</div>
            <div className="flex-1">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-2">
                <LayoutDashboard className="w-6 h-6 text-yellow-400" />
              </div>
              <p className="text-xs text-gray-400">View in admin</p>
            </div>
          </div>
        </div>

        {/* Prompts */}
        <h3 className="text-2xl font-bold text-gray-900 mb-6">The Prompts</h3>

        <PromptBlock
          title="Full Admin Dashboard"
          description="Complete setup with authentication, analytics, and access controls."
          prompt={`Add an admin dashboard to my website with:

1. AUTHENTICATION
   - Google login only
   - Restrict access to these emails: [YOUR EMAIL]
   - Only approved admins can access /admin

2. ANALYTICS DASHBOARD (default view)
   Track and display:
   - Live visitors (real-time)
   - Total page views with period comparison (vs last 7/30 days)
   - Unique visitors
   - Traffic sources breakdown:
     • Search engines (Google, Bing, DuckDuckGo)
     • Social media (Instagram, LinkedIn, Twitter/X, TikTok)
     • AI chat referrals (ChatGPT, Claude, Perplexity, Gemini, Copilot)
     • Direct traffic
     • Other referrals
   - Device breakdown (mobile/desktop percentage)
   - Top locations (country/city)
   - Peak traffic times (best day/hour)
   
   CTA Tracking - track clicks on:
   - [LIST YOUR MAIN CTAS, e.g.:]
   - "Work with me" button
   - "Newsletter signup" button
   - Social media links
   - Any external links

   Date filters: 24 hours, 7 days, 30 days, This year, All time, Custom range

3. ADMIN ACCESS CONTROLS
   - Settings tab to manage who can access admin
   - Primary admin (me) can add/remove other admin emails
   - Show list of approved admins

Store all data in the database. No external analytics needed.`}
        />

        <EmergentCTA />

        <PromptBlock
          title="Simple Analytics Only"
          description="Lightweight option without admin UI. Check the database directly."
          prompt={`Add basic analytics tracking to my website:

Track:
- Page views
- Button clicks on: [LIST YOUR BUTTONS]
- Traffic sources

Create an API endpoint I can query to see the data.
No admin UI needed - I'll check the database directly.`}
        />

        <PromptBlock
          title="YouTube Video Play Tracking"
          description="Add this to track which videos people watch."
          prompt={`Add tracking for YouTube video plays on my website.

When someone clicks play on an embedded YouTube video:
- Log which video was played (video ID or title)
- Log timestamp
- Show in my admin dashboard under "Video Plays"

I have these videos embedded:
- [VIDEO 1 TITLE/URL]
- [VIDEO 2 TITLE/URL]`}
        />

        <PromptBlock
          title="Newsletter Signup Tracking"
          description="Separate tracking for newsletter performance."
          prompt={`Track newsletter signup button clicks separately from other CTAs.

- Create a dedicated "Newsletter Clicks" metric in my dashboard
- Show total clicks and clicks over time
- My newsletter button links to: [YOUR BEEHIIV/SUBSTACK URL]`}
        />

        <AuthorCard author={resource.author} />
        
        <RelatedResources resourceIds={resource.relatedResources} />

        <AboutEmergent />
      </div>
    </div>
  );
};

export default AdminDashboardPage;
